import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStopRowKey } from './utils';
import {
  combineLocalDateAndTime,
  addOneDay,
  toUtcDateTimeString,
} from '../../../utils/datetime';
import {
  readDraft as readRaw,
  writeDraft as writeRaw,
  removeDraft as removeRaw,
} from './localStorageDraft';
import useDebouncedDraftWrite from './useDebouncedDraftWrite';

// Single source of truth for one route card's in-flight edits.
// Triggers map 1:1 to backend strategies:
//   - anchoredStops → ResequenceReroute (only these go in resequenceStops)
//   - removeSelections → RemoveStop
// `displayedSequence` is derived from anchors + original windows; never stored.
// `persistKey: { dropOffDate, routeName, previousRunId }` opts the hook into
// a debounced localStorage draft keyed by date + route name; the draft is
// discarded silently when the route's previousRunId moves on.

const STORAGE_PREFIX = 'route-ops:route-edits:';
const DEBOUNCE_MS = 300;

// Composite key the Nextmv backend uses to identify a stop inside a
// createNextmvRun input — confirmed by BE: stops are matched by
// `{location}#{stockingIndex}-{dropOffDate}`. `stop.id` is the location id;
// `stop.stockingDate` is the AWSDate (YYYY-MM-DD) already on the
// locationRoutes query.
// Returns null if any component is missing so the caller can skip the stop
// rather than ship a malformed key.
export function buildBackendStopId(stop) {
  if (!stop) return null;
  const location = stop.id;
  const index = stop.stockingIndex;
  const date = stop.stockingDate;
  if (!location || index == null || !date) return null;
  return `${location}#${index}-${date}`;
}

// Anchor windows are "HH:MM" in the supervisor's (browser-local) time; the
// backend passes resequence windows VERBATIM into the Nextmv payload
// (CreateNextmvRun → resequence_reroute), where every other stop window is
// a full UTC "YYYY-MM-DD HH:MM:SS" datetime. Combine the clock with the
// stop's stockingDate, roll an end at or before its start to the next day
// (cross-midnight windows, e.g. 13:00 – 01:00), and convert to UTC. The
// `<=` comparison is deliberate: equal start/end times mean a full 24h
// window (end rolls to the next day), NOT a zero-length window — keep `<=`,
// not `<`. Falls back to the raw string when the stop has no parseable
// stockingDate.
// Known limitation (spec decision): anchors always take their date from stockingDate — re-anchoring a window whose real start date differs (rare local/UTC date skew) lands it on the stocking date.
function anchorWindowToUtc(stop, anchor) {
  const start = combineLocalDateAndTime(
    stop.stockingDate,
    anchor.startTimeWindow,
  );
  let end = combineLocalDateAndTime(stop.stockingDate, anchor.endTimeWindow);
  if (start && end && end.getTime() <= start.getTime()) {
    end = addOneDay(end);
  }
  return {
    startTimeWindow: start
      ? toUtcDateTimeString(start)
      : anchor.startTimeWindow,
    endTimeWindow: end ? toUtcDateTimeString(end) : anchor.endTimeWindow,
  };
}

function effectiveStart(stop, anchor) {
  return (anchor && anchor.startTimeWindow) || stop.startTimeWindow || '￿';
}

function storageKey(dropOffDate, routeName) {
  if (!dropOffDate || !routeName) return null;
  return `${STORAGE_PREFIX}${dropOffDate}:${routeName}`;
}

// Reads the persisted draft and drops it when the staleness tag has moved on:
// a new previousRunId means the server rerouted, so any in-flight draft is
// unrelated to the current route state.
function readRouteDraft({ dropOffDate, routeName, previousRunId }) {
  const key = storageKey(dropOffDate, routeName);
  if (!key) return null;
  const parsed = readRaw(key);
  if (!parsed) return null;
  if (parsed.previousRunId !== previousRunId) {
    removeRaw(key);
    return null;
  }
  return parsed;
}

function mapFromObject(obj) {
  const m = new Map();
  if (!obj) return m;
  Object.entries(obj).forEach(([k, v]) => m.set(k, v));
  return m;
}

function mapToObject(m) {
  const out = {};
  m.forEach((v, k) => {
    out[k] = v;
  });
  return out;
}

export default function useRouteEdits({ stops, persistKey = null }) {
  // Destructure to primitives so useMemo/useEffect dep arrays are stable
  // values ESLint can reason about — avoids needing exhaustive-deps disables.
  const pkDropOffDate = persistKey ? persistKey.dropOffDate : null;
  const pkRouteName = persistKey ? persistKey.routeName : null;
  const pkPreviousRunId = persistKey ? persistKey.previousRunId : null;

  const stopByKey = useMemo(() => {
    const m = new Map();
    stops.forEach((s) => m.set(getStopRowKey(s), s));
    return m;
  }, [stops]);

  // Rehydrate on first mount and whenever the staleness tag (previousRunId)
  // shifts — a new run id means the server moved on; any in-flight draft is
  // unrelated to the current route state.
  const initialDraft = useMemo(
    () =>
      readRouteDraft({
        dropOffDate: pkDropOffDate,
        routeName: pkRouteName,
        previousRunId: pkPreviousRunId,
      }),
    [pkDropOffDate, pkRouteName, pkPreviousRunId],
  );

  const [anchoredStops, setAnchoredStops] = useState(() =>
    mapFromObject(initialDraft?.anchoredStops),
  );
  const [removeSelections, setRemoveSelections] = useState(() =>
    mapFromObject(initialDraft?.removeSelections),
  );

  // Single write-skip gate consumed by the shared debounce hook. Initialized
  // true so a clean mount never writes; the rehydration effect below sets it
  // true again so a re-rehydrate isn't echoed straight back to storage.
  const skipWriteRef = useRef(true);

  // Rehydration effect: re-reads localStorage whenever pkPreviousRunId (or
  // other key fields) changes after mount. useState lazy initializers only run
  // once, so without this effect a parent re-render with a new previousRunId
  // would leave stale in-memory anchors showing.
  const isFirstRehydrate = useRef(true);

  useEffect(() => {
    if (isFirstRehydrate.current) {
      isFirstRehydrate.current = false;
      return;
    }
    const next = readRouteDraft({
      dropOffDate: pkDropOffDate,
      routeName: pkRouteName,
      previousRunId: pkPreviousRunId,
    });
    skipWriteRef.current = true;
    setAnchoredStops(mapFromObject(next?.anchoredStops));
    setRemoveSelections(mapFromObject(next?.removeSelections));
  }, [pkDropOffDate, pkRouteName, pkPreviousRunId]);

  // Debounced persist via the shared hook. A clean edit set removes the draft
  // rather than writing an empty one; otherwise the anchors/removes are
  // snapshotted under the current previousRunId staleness tag.
  const persist = useCallback(() => {
    const key = storageKey(pkDropOffDate, pkRouteName);
    if (!key) return;
    if (anchoredStops.size === 0 && removeSelections.size === 0) {
      removeRaw(key);
      return;
    }
    writeRaw(
      key,
      {
        previousRunId: pkPreviousRunId || null,
        anchoredStops: mapToObject(anchoredStops),
        removeSelections: mapToObject(removeSelections),
      },
      'route-edits draft write failed',
    );
  }, [
    anchoredStops,
    removeSelections,
    pkDropOffDate,
    pkRouteName,
    pkPreviousRunId,
  ]);

  useDebouncedDraftWrite(persist, {
    debounceMs: DEBOUNCE_MS,
    skipRef: skipWriteRef,
  });

  const displayedSequence = useMemo(() => {
    const keys = [...stopByKey.keys()];
    return keys.sort((aKey, bKey) => {
      const aStop = stopByKey.get(aKey);
      const bStop = stopByKey.get(bKey);
      const aStart = effectiveStart(aStop, anchoredStops.get(aKey));
      const bStart = effectiveStart(bStop, anchoredStops.get(bKey));
      return aStart.localeCompare(bStart) || aKey.localeCompare(bKey);
    });
  }, [stopByKey, anchoredStops]);

  const isDirty = anchoredStops.size > 0 || removeSelections.size > 0;

  const setAnchor = useCallback((stopKey, anchor) => {
    setAnchoredStops((prev) => {
      const next = new Map(prev);
      next.set(stopKey, anchor);
      return next;
    });
  }, []);

  const unsetAnchor = useCallback((stopKey) => {
    setAnchoredStops((prev) => {
      if (!prev.has(stopKey)) return prev;
      const next = new Map(prev);
      next.delete(stopKey);
      return next;
    });
  }, []);

  const toggleRemove = useCallback((stopKey, checked, reason = null) => {
    setRemoveSelections((prev) => {
      const next = new Map(prev);
      if (checked) {
        next.set(stopKey, { reason: reason || { value: '', note: '' } });
      } else {
        next.delete(stopKey);
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setAnchoredStops(new Map());
    setRemoveSelections(new Map());
    // Explicit removal so an empty draft is never left behind after a
    // confirmed reroute or per-card Cancel.
    removeRaw(storageKey(pkDropOffDate, pkRouteName));
  }, [pkDropOffDate, pkRouteName]);

  const serialize = useCallback(() => {
    const resequenceStops = [];

    anchoredStops.forEach((anchor, stopKey) => {
      const stop = stopByKey.get(stopKey);
      if (!stop) return;
      const stopId = buildBackendStopId(stop);
      if (!stopId) return;
      resequenceStops.push({
        stopId,
        ...anchorWindowToUtc(stop, anchor),
      });
    });

    const removedStops = [];
    const removeReasonsByStopId = {};
    removeSelections.forEach((entry, stopKey) => {
      const stop = stopByKey.get(stopKey);
      if (!stop) return;
      const stopId = buildBackendStopId(stop);
      if (!stopId) return;
      removedStops.push(stopId);
      removeReasonsByStopId[stopId] = {
        reason: entry.reason?.value || '',
        notes: entry.reason?.note || '',
        locationId: stop.id || '',
      };
    });

    return {
      resequenceStops,
      removedStops,
      removeReasonsByStopId,
    };
  }, [anchoredStops, removeSelections, stopByKey]);

  return {
    anchoredStops,
    removeSelections,
    displayedSequence,
    stopByKey,
    isDirty,
    setAnchor,
    unsetAnchor,
    toggleRemove,
    reset,
    serialize,
  };
}
